import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  User,
  Building2,
  Globe2,
  Mail,
  Coins,
  Languages,
  Palette,
  Image as ImageIcon,
  FileText,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { listLegalTexts, deleteLegalText } from "@/lib/legal";

const LOCALES = [
  { value: "fr", label: "Français" },
  { value: "ar", label: "العربية (Arabe)" },
  { value: "en", label: "English" },
];

const CURRENCIES = [
  { code: "UM", symbol: "UM", label: "UM (ouguiya)" },
  { code: "CFA", symbol: "F", label: "CFA (franc CFA)" },
  { code: "EUR", symbol: "€", label: "EUR (euro)" },
  { code: "USD", symbol: "$", label: "USD (dollar)" },
  { code: "MAD", symbol: "DH", label: "MAD (dirham)" },
];

export default function Settings() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: "",
    organization: "",
    country: "",
    locale: "fr",
    default_currency: "",
    currency_symbol: "",
    logo_url: "",
    report_primary_color: "#1A3C5E",
    report_header_note: "",
  });
  const [saving, setSaving] = useState(null); // 'profile' | 'prefs' | 'template'
  const [legalTexts, setLegalTexts] = useState([]);
  const [loadingLegal, setLoadingLegal] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      organization: user.organization || "",
      country: user.country || "",
      locale: user.locale || "fr",
      default_currency: user.default_currency || "",
      currency_symbol: user.currency_symbol || "",
      logo_url: user.logo_url || "",
      report_primary_color: user.report_primary_color || "#1A3C5E",
      report_header_note: user.report_header_note || "",
    });
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const items = await listLegalTexts();
        setLegalTexts(items || []);
      } catch (e) {
        /* ignore */
      } finally {
        setLoadingLegal(false);
      }
    })();
  }, []);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const saveSection = (section) => async (e) => {
    e.preventDefault();
    setSaving(section);
    let payload = {};
    if (section === "profile") {
      payload = {
        name: form.name.trim() || undefined,
        organization: form.organization.trim() || undefined,
        country: form.country.trim() || undefined,
      };
    } else if (section === "prefs") {
      payload = {
        locale: form.locale,
        default_currency: form.default_currency || undefined,
        currency_symbol: form.currency_symbol || undefined,
      };
    } else if (section === "template") {
      payload = {
        logo_url: form.logo_url?.trim() || undefined,
        report_primary_color: form.report_primary_color || undefined,
        report_header_note: form.report_header_note?.trim() || undefined,
      };
    }
    const res = await updateProfile(payload);
    setSaving(null);
    if (res.ok) {
      toast.success("Paramètres enregistrés.");
    } else {
      toast.error(res.error || "Échec de la mise à jour.");
    }
  };

  const applyCurrencyPreset = (code) => {
    const entry = CURRENCIES.find((c) => c.code === code);
    if (!entry) return;
    setForm((f) => ({
      ...f,
      default_currency: entry.code,
      currency_symbol: entry.symbol,
    }));
  };

  const handleDeleteLegal = async (id) => {
    if (!window.confirm("Supprimer ce texte juridique de votre bibliothèque ?"))
      return;
    setDeletingId(id);
    try {
      await deleteLegalText(id);
      setLegalTexts((prev) => prev.filter((x) => x.id !== id));
      toast.success("Texte juridique supprimé.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Paramètres
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Mon profil et préférences
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Ces informations personnalisent les rapports institutionnels générés
          depuis VITAE-PUBLICA™. La bibliothèque juridique est partagée entre
          tous vos projets.
        </p>
      </div>

      {/* Profile */}
      <Card data-testid="settings-profile-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
            Informations personnelles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={saveSection("profile")}
            className="space-y-4"
            data-testid="settings-profile-form"
          >
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-muted/40 pl-9"
                  data-testid="profile-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="name"
                    value={form.name}
                    onChange={onChange("name")}
                    className="pl-9"
                    data-testid="profile-name-input"
                    maxLength={80}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="organization">Organisation</Label>
                <div className="relative mt-1.5">
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="organization"
                    value={form.organization}
                    onChange={onChange("organization")}
                    className="pl-9"
                    data-testid="profile-org-input"
                    maxLength={120}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="country">Pays par défaut</Label>
                <div className="relative mt-1.5">
                  <Globe2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="country"
                    value={form.country}
                    onChange={onChange("country")}
                    className="pl-9"
                    data-testid="profile-country-input"
                    maxLength={60}
                  />
                </div>
              </div>
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving === "profile"}
                data-testid="profile-save-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving === "profile" ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Locale + currency */}
      <Card data-testid="settings-prefs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
            Langue et monnaie
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={saveSection("prefs")}
            className="space-y-4"
            data-testid="settings-prefs-form"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label>Langue de l'interface et des rapports</Label>
                <Select
                  value={form.locale}
                  onValueChange={(v) => setForm((f) => ({ ...f, locale: v }))}
                >
                  <SelectTrigger
                    className="mt-1.5"
                    data-testid="prefs-locale-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCALES.map((l) => (
                      <SelectItem
                        key={l.value}
                        value={l.value}
                        data-testid={`prefs-locale-${l.value}`}
                      >
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  La traduction complète EN/AR est en cours de déploiement.
                  Actuellement, FR est l'interface principale.
                </p>
              </div>
              <div>
                <Label>Monnaie par défaut (code)</Label>
                <Select
                  value={form.default_currency || ""}
                  onValueChange={(v) => {
                    applyCurrencyPreset(v);
                  }}
                >
                  <SelectTrigger
                    className="mt-1.5"
                    data-testid="prefs-currency-trigger"
                  >
                    <SelectValue placeholder="Choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem
                        key={c.code}
                        value={c.code}
                        data-testid={`prefs-currency-${c.code}`}
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="currency-symbol">Symbole</Label>
                <div className="relative mt-1.5">
                  <Coins className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="currency-symbol"
                    value={form.currency_symbol}
                    onChange={onChange("currency_symbol")}
                    className="pl-9"
                    maxLength={5}
                    data-testid="prefs-currency-symbol-input"
                  />
                </div>
              </div>
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving === "prefs"}
                data-testid="prefs-save-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving === "prefs" ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Report template */}
      <Card data-testid="settings-template-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
            Modèle de rapport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={saveSection("template")}
            className="space-y-4"
            data-testid="settings-template-form"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="logo-url">Logo de l'organisation (URL)</Label>
                <div className="relative mt-1.5">
                  <ImageIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="logo-url"
                    type="url"
                    value={form.logo_url}
                    onChange={onChange("logo_url")}
                    className="pl-9"
                    placeholder="https://…/logo.png"
                    maxLength={500}
                    data-testid="template-logo-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="primary-color">Couleur primaire des rapports</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <input
                    id="primary-color"
                    type="color"
                    value={form.report_primary_color}
                    onChange={onChange("report_primary_color")}
                    className="h-10 w-14 cursor-pointer rounded border border-border bg-transparent"
                    data-testid="template-color-picker"
                  />
                  <Input
                    value={form.report_primary_color}
                    onChange={onChange("report_primary_color")}
                    maxLength={7}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="font-mono"
                    data-testid="template-color-hex"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="header-note">En-tête personnalisé (optionnel)</Label>
              <Input
                id="header-note"
                value={form.report_header_note}
                onChange={onChange("report_header_note")}
                placeholder="Ex : Chaire VITAE-PUBLICA · Université de Nouakchott · 2026"
                maxLength={400}
                data-testid="template-header-input"
                className="mt-1.5"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Ce texte apparaîtra en en-tête de vos rapports PDF / Word.
              </p>
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving === "template"}
                data-testid="template-save-btn"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving === "template" ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Legal corpus library */}
      <Card data-testid="settings-legal-library-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
            Bibliothèque juridique
            <span className="ml-2 rounded-full bg-muted px-2 py-0.5 font-mono text-xs">
              {legalTexts.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">
            Les textes juridiques que vous uploadez sont partagés entre tous
            vos projets. Vous pouvez les gérer ici ou depuis l'étape 5 du wizard.
          </p>
          {loadingLegal ? (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          ) : legalTexts.length === 0 ? (
            <div
              className="rounded-md border border-dashed border-border bg-muted/20 p-5 text-center text-sm text-muted-foreground"
              data-testid="legal-library-empty"
            >
              Aucun texte juridique dans votre bibliothèque.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="legal-library-list">
              {legalTexts.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 rounded-md border border-border bg-card p-3"
                  data-testid={`legal-library-row-${t.id}`}
                >
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {t.title}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t.text_type}
                      {t.jurisdiction && ` · ${t.jurisdiction}`}
                      {t.page_count != null && ` · ${t.page_count} p.`}
                      {t.created_at &&
                        ` · ${new Date(t.created_at).toLocaleDateString("fr-FR")}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteLegal(t.id)}
                    disabled={deletingId === t.id}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    data-testid={`legal-library-delete-${t.id}`}
                  >
                    {deletingId === t.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Account status */}
      <Card data-testid="settings-meta-card">
        <CardHeader>
          <CardTitle>Statut du compte</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-3">
            <span className="text-muted-foreground">Rôle</span>
            <span className="font-semibold">{user?.role || "researcher"}</span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-3">
            <span className="text-muted-foreground">Langue</span>
            <span className="font-semibold uppercase">
              {user?.locale || "fr"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-3">
            <span className="text-muted-foreground">Inscrit depuis</span>
            <span className="font-semibold">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString("fr-FR")
                : "—"}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
